/**
 * Shrinks a camera or gallery photo in the browser before it is uploaded (PLAN §6, Photos).
 *
 * At most 1600 px on the long edge, re-encoded as JPEG at quality 0.8, with createImageBitmap and a
 * canvas — no library. That keeps a phone photo a few hundred kB (the bucket's limit is 10 MiB and
 * the yard's signal is poor), turns an iPhone capture into a JPEG the bucket accepts, and drops the
 * EXIF block, location included. A photo the browser cannot decode — a gallery HEIC in Chrome —
 * fails with UnreadableImageError and a sentence the inspector can act on.
 *
 * The decode and encode steps are injected so the sizing and failure rules run under vitest's node
 * environment; `browserDownscaleDeps` is the real pair.
 */

export const MAX_EDGE_PX = 1600;
export const JPEG_QUALITY = 0.8;

export const UNREADABLE_IMAGE_SENTENCE =
    'This photo is in a format the browser cannot open (often HEIC from the gallery). Take it with the camera button, or choose a JPEG.';

export class UnreadableImageError extends Error {
    constructor() {
        super(UNREADABLE_IMAGE_SENTENCE);
        this.name = 'UnreadableImageError';
    }
}

/** The size to draw at: the long edge at most `maxEdge`, the aspect kept, never enlarged. */
export function fitWithin(width: number, height: number, maxEdge: number = MAX_EDGE_PX): { width: number; height: number } {
    if (!(width > 0) || !(height > 0)) throw new RangeError('An image has a positive width and height');
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

export interface DecodedImage {
    width: number;
    height: number;
    source: CanvasImageSource;
    close(): void;
}

export interface DownscaleDeps {
    decode(blob: Blob): Promise<DecodedImage>;
    encode(source: CanvasImageSource, width: number, height: number, quality: number): Promise<Blob>;
}

async function decodeWithImageElement(blob: Blob): Promise<DecodedImage> {
    const url = URL.createObjectURL(blob);
    try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        return { width: img.naturalWidth, height: img.naturalHeight, source: img, close: () => URL.revokeObjectURL(url) };
    } catch (error) {
        URL.revokeObjectURL(url);
        throw error;
    }
}

export const browserDownscaleDeps: DownscaleDeps = {
    async decode(blob) {
        if (typeof createImageBitmap !== 'function') return decodeWithImageElement(blob);
        let bitmap: ImageBitmap;
        try {
            // EXIF rotation applied, so a portrait capture is not drawn on its side.
            bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
        } catch {
            // Older engines reject the options bag itself; a HEIC fails here too and is rethrown.
            bitmap = await createImageBitmap(blob);
        }
        return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
    },
    encode(source, width, height, quality) {
        return new Promise<Blob>((resolve, reject) => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('The browser could not prepare the photo.'));
                return;
            }
            ctx.drawImage(source, 0, 0, width, height);
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('The browser could not prepare the photo.'))),
                'image/jpeg',
                quality,
            );
        });
    },
};

/** The photo as a JPEG of at most `maxEdge` px; UnreadableImageError when it cannot be decoded. */
export async function downscaleImage(
    file: Blob,
    options: { maxEdge?: number; quality?: number; deps?: DownscaleDeps } = {},
): Promise<Blob> {
    const { maxEdge = MAX_EDGE_PX, quality = JPEG_QUALITY, deps = browserDownscaleDeps } = options;
    let decoded: DecodedImage;
    try {
        decoded = await deps.decode(file);
    } catch {
        throw new UnreadableImageError();
    }
    try {
        if (!(decoded.width > 0) || !(decoded.height > 0)) throw new UnreadableImageError();
        const size = fitWithin(decoded.width, decoded.height, maxEdge);
        return await deps.encode(decoded.source, size.width, size.height, quality);
    } finally {
        decoded.close();
    }
}
